import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Clock, DoorOpen, ShoppingBag, CreditCard, Trash2, Printer, Gift } from 'lucide-react';
import CashCalculator from '../components/CashCalculator';

const catStyles = {
  fundador: 'bg-purple-100 text-purple-800',
  normal: 'bg-stone-100 text-stone-800',
  frecuente: 'bg-orange-100 text-orange-800',
  eventos: 'bg-blue-100 text-blue-800',
};
const paymentMethods = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'cortesia', label: 'Cortesia' },
  { value: 'cupon', label: 'Cupon' },
];

function getDuration(entryTime) {
  if (!entryTime) return '0m';
  const diff = Date.now() - new Date(entryTime).getTime();
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

export default function VisitsPage() {
  const { API, user: currentUser } = useAuth();
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [checkinForm, setCheckinForm] = useState({ client_id: '', waiter_id: '', locker_number: '', bracelet_code: '', notes: '' });
  const [orderForm, setOrderForm] = useState({ product_id: '', quantity: 1 });
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [availableLockers, setAvailableLockers] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showExperience, setShowExperience] = useState(false);
  const [expType, setExpType] = useState('');
  const [expReason, setExpReason] = useState('');
  const [expOrderId, setExpOrderId] = useState('');

  const fetchVisits = useCallback(() => {
    axios.get(`${API}/visits/active`).then(r => setVisits(r.data)).catch(() => {});
  }, [API]);

  const fetchHelpers = useCallback(() => {
    Promise.all([
      axios.get(`${API}/clients`),
      axios.get(`${API}/staff/waiters`),
      axios.get(`${API}/products`),
      axios.get(`${API}/settings`),
      axios.get(`${API}/lockers/available`)
    ]).then(([c, w, p, s, l]) => {
      setClients(c.data);
      setWaiters(w.data);
      setProducts(p.data.filter(pr => pr.active));
      setSettings(s.data);
      setAvailableLockers(l.data.available || []);
    }).catch(() => {});
  }, [API]);

  useEffect(() => { fetchVisits(); fetchHelpers(); }, [fetchVisits, fetchHelpers]);
  useEffect(() => { const interval = setInterval(fetchVisits, 30000); return () => clearInterval(interval); }, [fetchVisits]);

  const handleCheckin = async () => {
    if (!checkinForm.client_id || !checkinForm.waiter_id) { toast.error('Selecciona cliente y mesero'); return; }
    try {
      await axios.post(`${API}/visits/checkin`, {
        ...checkinForm,
        locker_number: checkinForm.locker_number && checkinForm.locker_number !== 'none' ? parseInt(checkinForm.locker_number) : null
      });
      toast.success('Entrada registrada');
      setShowCheckin(false);
      setCheckinForm({ client_id: '', waiter_id: '', locker_number: '', bracelet_code: '', notes: '' });
      fetchVisits();
      fetchHelpers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleAddOrder = async () => {
    if (!orderForm.product_id || orderForm.quantity < 1) { toast.error('Selecciona producto y cantidad'); return; }
    try {
      const { data } = await axios.post(`${API}/visits/${selectedVisit.id}/orders`, orderForm);
      toast.success('Producto agregado');
      setSelectedVisit(data);
      setShowAddOrder(false);
      setOrderForm({ product_id: '', quantity: 1 });
      fetchVisits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleRemoveOrder = async (orderId) => {
    setCancelOrderId(orderId);
    setCancelReason('');
    setShowCancelDialog(true);
  };

  const confirmCancelOrder = async () => {
    if (!cancelReason.trim()) { toast.error('Ingresa la razon de cancelacion'); return; }
    try {
      const { data } = await axios.delete(`${API}/visits/${selectedVisit.id}/orders/${cancelOrderId}`, {
        data: { reason: cancelReason }
      });
      toast.success('Producto cancelado');
      setSelectedVisit(data);
      setShowCancelDialog(false);
      setCancelOrderId(null);
      setCancelReason('');
      fetchVisits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleCheckout = async () => {
    try {
      const { data } = await axios.post(`${API}/visits/${selectedVisit.id}/checkout`, { payment_method: paymentMethod, notes: '' });
      toast.success('Cobro realizado exitosamente');
      setShowCheckout(false);
      const { data: receipt } = await axios.get(`${API}/visits/${selectedVisit.id}/receipt`);
      setReceiptData(receipt);
      setShowReceipt(true);
      setSelectedVisit(null);
      fetchVisits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleExperience = async () => {
    if (!expType || !expReason) { toast.error('Selecciona tipo y razon'); return; }
    if (expType === 'cancel_product' && !expOrderId) { toast.error('Selecciona el producto a cancelar'); return; }
    try {
      const body = { type: expType, reason: expReason };
      if (expType === 'cancel_product') body.order_id = expOrderId;
      const { data } = await axios.post(`${API}/visits/${selectedVisit.id}/experience`, body);
      toast.success('Experiencia aplicada');
      setSelectedVisit(data.visit);
      setShowExperience(false);
      setExpType(''); setExpReason(''); setExpOrderId('');
      fetchVisits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const printReceipt = () => {
    if (!receiptData) return;
    const payLabels = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', cortesia: 'Cortesia', cupon: 'Cupon' };
    const formatTime = (iso) => iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '';
    const orders = (receiptData.orders || []).map(o =>
      `<tr><td style="padding:4px 0;border-bottom:1px dashed #ddd">${o.product_name}</td><td style="text-align:center;border-bottom:1px dashed #ddd">${o.quantity}</td><td style="text-align:right;border-bottom:1px dashed #ddd">$${o.total.toFixed(2)}</td></tr>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><title>Ticket</title><style>body{font-family:monospace;width:280px;margin:0 auto;padding:16px;font-size:12px}h2{text-align:center;margin:0 0 4px}p{margin:2px 0}table{width:100%;border-collapse:collapse;margin:8px 0}.total{font-size:16px;font-weight:bold;text-align:right;border-top:2px solid #333;padding-top:6px;margin-top:8px}.center{text-align:center}.divider{border-top:1px dashed #999;margin:8px 0}@media print{body{margin:0}}</style></head><body>
    <h2>${receiptData.club_name}</h2><p class="center">Sistema de Administracion</p>
    <div class="divider"></div>
    <p><strong>Cliente:</strong> ${receiptData.client_name} (${receiptData.client_category})</p>
    <p><strong>Entrada:</strong> ${formatTime(receiptData.entry_time)}</p>
    <p><strong>Salida:</strong> ${formatTime(receiptData.exit_time)}</p>
    <p><strong>Mesero:</strong> ${receiptData.waiter_name}</p>
    <div class="divider"></div>
    <table><tr><th style="text-align:left">Producto</th><th style="text-align:center">Cant</th><th style="text-align:right">Total</th></tr>${orders}</table>
    <div class="divider"></div>
    <p style="text-align:right">Subtotal: $${receiptData.subtotal.toFixed(2)}</p>
    ${receiptData.discount_percent > 0 ? `<p style="text-align:right;color:#C2410C">Descuento (${receiptData.discount_percent}%): -$${receiptData.discount_amount.toFixed(2)}</p>` : ''}
    <p class="total">Total: $${receiptData.total.toFixed(2)}</p>
    <p style="text-align:right"><strong>Pago:</strong> ${payLabels[receiptData.payment_method] || receiptData.payment_method}</p>
    <p><strong>Cajero:</strong> ${receiptData.cashier_name}</p>
    <div class="divider"></div>
    <p class="center" style="margin-top:12px">Gracias por su visita</p>
    <p class="center" style="font-size:10px">${formatTime(receiptData.date)}</p>
    </body></html>`;
    const w = window.open('', '', 'width=320,height=600');
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const canCheckout = currentUser?.role === 'admin' || currentUser?.role === 'cajero';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="visits-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Visitas Activas</h1>
          <p className="text-stone-500 text-sm mt-1">{visits.length} visitas en curso</p>
        </div>
        <Button data-testid="checkin-btn" onClick={() => setShowCheckin(true)} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full px-6 shadow-sm hover:shadow-md active:scale-[0.98] transition-all">
          <DoorOpen className="w-4 h-4 mr-2" /> Registrar Entrada
        </Button>
      </div>

      {visits.length === 0 ? (
        <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
          <CardContent className="p-12 text-center">
            <DoorOpen className="w-12 h-12 text-stone-300 mx-auto mb-3" strokeWidth={1} />
            <p className="text-stone-500 text-sm">No hay visitas activas</p>
            <p className="text-stone-400 text-xs mt-1">Registra una entrada para comenzar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {visits.map(v => (
            <Card key={v.id} data-testid={`visit-card-${v.id}`}
              className="bg-white border border-stone-200 shadow-sm rounded-2xl hover:shadow-md hover:border-orange-200 transition-all cursor-pointer"
              onClick={() => { setSelectedVisit(v); }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-stone-900 text-sm">{v.client_name}</p>
                    <Badge className={`${catStyles[v.client_category] || catStyles.normal} border-0 rounded-full text-xs mt-1`}>{v.client_category}</Badge>
                  </div>
                  <div className="flex items-center gap-1 text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                    <Clock className="w-3 h-3" />
                    <span className="text-xs font-medium">{getDuration(v.entry_time)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-stone-500 mt-3 pt-3 border-t border-stone-100">
                  <span>Mesero: {v.waiter_name}</span>
                  <span className="font-semibold text-stone-900 text-sm">${(v.total || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-stone-400 mt-1">
                  <span>{(v.orders || []).length} productos</span>
                  {v.locker_number && <span className="bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full font-medium">Locker #{v.locker_number}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Check-in Dialog */}
      <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Registrar Entrada</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Cliente</Label>
              <Select value={checkinForm.client_id} onValueChange={v => setCheckinForm({ ...checkinForm, client_id: v })}>
                <SelectTrigger data-testid="checkin-client-select" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.category})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mesero</Label>
              <Select value={checkinForm.waiter_id} onValueChange={v => setCheckinForm({ ...checkinForm, waiter_id: v })}>
                <SelectTrigger data-testid="checkin-waiter-select" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar mesero" /></SelectTrigger>
                <SelectContent>{waiters.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Locker</Label>
              <Select value={checkinForm.locker_number} onValueChange={v => setCheckinForm({ ...checkinForm, locker_number: v })}>
                <SelectTrigger data-testid="checkin-locker-select" className="rounded-xl mt-1"><SelectValue placeholder="Sin locker" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin locker</SelectItem>
                  {availableLockers.map(n => <SelectItem key={n} value={String(n)}>Locker #{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-400 mt-1">{availableLockers.length} lockers disponibles</p>
            </div>
            <div>
              <Label>Codigo Pulsera RFID</Label>
              <Input data-testid="checkin-bracelet" value={checkinForm.bracelet_code} onChange={e => setCheckinForm({ ...checkinForm, bracelet_code: e.target.value })} placeholder="Escanear o escribir codigo..." className="rounded-xl mt-1" />
              <p className="text-xs text-stone-400 mt-1">Opcional. Para consulta rapida por pulsera</p>
            </div>
            <div><Label>Notas</Label><Input data-testid="checkin-notes" value={checkinForm.notes} onChange={e => setCheckinForm({ ...checkinForm, notes: e.target.value })} className="rounded-xl mt-1" /></div>
            {checkinForm.client_id && settings && (() => {
              const cl = clients.find(c => c.id === checkinForm.client_id);
              const cat = cl?.category || 'normal';
              const fee = settings.entry_fees?.[cat] || 0;
              const disc = settings.category_discounts?.[cat] || 0;
              return (
                <div className="bg-stone-50 rounded-xl p-3 space-y-1">
                  <p className="text-xs text-stone-500">Cuota de entrada: <strong className="text-stone-900">{fee > 0 ? `$${fee}` : 'Sin cuota'}</strong></p>
                  <p className="text-xs text-stone-500">Descuento al cobro: <strong className="text-orange-700">{disc > 0 ? `${disc}%` : 'Sin descuento'}</strong></p>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckin(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="checkin-submit" onClick={handleCheckin} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visit Detail Sheet */}
      <Sheet open={!!selectedVisit} onOpenChange={(open) => { if (!open) setSelectedVisit(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedVisit && (
            <div className="space-y-6">
              <SheetHeader>
                <SheetTitle className="font-outfit text-xl">{selectedVisit.client_name}</SheetTitle>
              </SheetHeader>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Entrada</p>
                  <p className="text-sm font-medium text-stone-900 mt-1">{new Date(selectedVisit.entry_time).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Duracion</p>
                  <p className="text-sm font-medium text-stone-900 mt-1">{getDuration(selectedVisit.entry_time)}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Mesero</p>
                  <p className="text-sm font-medium text-stone-900 mt-1">{selectedVisit.waiter_name}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-500 uppercase tracking-wider">Categoria</p>
                  <Badge className={`${catStyles[selectedVisit.client_category] || catStyles.normal} border-0 rounded-full text-xs mt-1`}>{selectedVisit.client_category}</Badge>
                </div>
                {selectedVisit.locker_number && (
                  <div className="bg-stone-50 rounded-xl p-3 col-span-2">
                    <p className="text-xs text-stone-500 uppercase tracking-wider">Locker</p>
                    <p className="text-sm font-medium text-stone-900 mt-1">#{selectedVisit.locker_number}</p>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-outfit font-medium text-stone-900">Consumos</h3>
                  <Button data-testid="add-order-btn" onClick={() => setShowAddOrder(true)} size="sm" className="bg-orange-700 hover:bg-orange-800 text-white rounded-full text-xs h-8 px-4">
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                </div>
                <div className="space-y-2">
                  {(selectedVisit.orders || []).map(o => (
                    <div key={o.id} className="flex items-center justify-between bg-stone-50 rounded-xl p-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900">{o.product_name}</p>
                        <p className="text-xs text-stone-500">{o.quantity} x ${o.unit_price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-stone-900">${o.total.toFixed(2)}</span>
                        {canCheckout && (
                          <button data-testid={`remove-order-${o.id}`} onClick={() => handleRemoveOrder(o.id)} className="p-1 rounded-lg hover:bg-red-100 text-stone-400 hover:text-red-600 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(selectedVisit.orders || []).length === 0 && <p className="text-sm text-stone-400 text-center py-4">Sin consumos</p>}
                </div>
              </div>

              <div className="border-t border-stone-200 pt-4">
                {selectedVisit.experience_discount > 0 && (
                  <div className="flex items-center justify-between mb-2 text-sm">
                    <span className="text-purple-700">Cortesia aplicada</span>
                    <span className="text-purple-700 font-medium">-${selectedVisit.experience_discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <span className="font-outfit text-lg font-semibold text-stone-900">Total</span>
                  <span className="font-outfit text-2xl font-bold text-orange-700">${(selectedVisit.total || 0).toFixed(2)}</span>
                </div>
                {canCheckout && (
                  <div className="flex gap-2">
                    <Button data-testid="experience-btn" onClick={() => setShowExperience(true)} className="flex-1 bg-purple-700 hover:bg-purple-800 text-white rounded-full h-11 font-medium transition-all">
                      <Gift className="w-4 h-4 mr-2" /> Experiencia
                    </Button>
                    <Button data-testid="checkout-open-btn" onClick={() => setShowCheckout(true)} className="flex-1 bg-green-700 hover:bg-green-800 text-white rounded-full h-11 font-medium transition-all shadow-sm hover:shadow-md active:scale-[0.98]">
                      <CreditCard className="w-4 h-4 mr-2" /> Cobrar
                    </Button>
                  </div>
                )}
                {!canCheckout && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 text-center">Solo cajero o administrador puede cobrar</p>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Add Order Dialog */}
      <Dialog open={showAddOrder} onOpenChange={setShowAddOrder}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Agregar Producto</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Producto</Label>
              <Select value={orderForm.product_id} onValueChange={v => setOrderForm({ ...orderForm, product_id: v })}>
                <SelectTrigger data-testid="order-product-select" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - ${p.price.toFixed(2)} {p.stock !== null && p.stock !== undefined ? `(${p.stock} disp.)` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cantidad</Label>
              <Input data-testid="order-quantity" type="number" min="1" value={orderForm.quantity} onChange={e => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) || 1 })} className="rounded-xl mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOrder(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="order-submit" onClick={handleAddOrder} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Cobrar Visita</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {selectedVisit && (() => {
              const discPct = settings?.category_discounts?.[selectedVisit.client_category] || 0;
              const subtotal = selectedVisit.subtotal || 0;
              const discAmt = Math.round(subtotal * discPct / 100 * 100) / 100;
              const experienceDiscount = selectedVisit.experience_discount || 0;
              let finalTotal = Math.round((subtotal - discAmt - experienceDiscount) * 100) / 100;
              if (finalTotal < 0) finalTotal = 0;
              return (
                <div className="text-center py-2 space-y-2">
                  <p className="text-stone-500 text-sm">Subtotal</p>
                  <p className="font-outfit text-2xl font-semibold text-stone-700">${subtotal.toFixed(2)}</p>
                  {discPct > 0 && (
                    <p className="text-orange-700 text-sm font-medium">Descuento {discPct}% ({selectedVisit.client_category}): -${discAmt.toFixed(2)}</p>
                  )}
                  {experienceDiscount > 0 && (
                    <p className="text-purple-700 text-sm font-medium">Cortesia aplicada: -${experienceDiscount.toFixed(2)}</p>
                  )}
                  <p className="text-stone-500 text-sm mt-2">Total a cobrar</p>
                  <p className="font-outfit text-4xl font-bold text-orange-700">${finalTotal.toFixed(2)}</p>
                </div>
              );
            })()}
            <div>
              <Label>Metodo de Pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="checkout-payment-select" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{paymentMethods.map(pm => <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {paymentMethod === 'efectivo' && selectedVisit && (() => {
              const dp = settings?.category_discounts?.[selectedVisit.client_category] || 0;
              const st = selectedVisit.subtotal || 0;
              const ed = selectedVisit.experience_discount || 0;
              const ft = Math.max(0, Math.round((st - st * dp / 100 - ed) * 100) / 100);
              return <CashCalculator total={ft} />;
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="checkout-submit" onClick={handleCheckout} className="bg-green-700 hover:bg-green-800 text-white rounded-full">Confirmar Cobro</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader><DialogTitle className="font-outfit">Ticket de Cobro</DialogTitle></DialogHeader>
          {receiptData && (
            <div className="space-y-3 py-2 text-sm">
              <div className="text-center border-b border-dashed border-stone-300 pb-3">
                <p className="font-outfit font-semibold text-lg text-stone-900">{receiptData.club_name}</p>
                <p className="text-xs text-stone-500">{receiptData.date ? new Date(receiptData.date).toLocaleString('es-MX') : ''}</p>
              </div>
              <div className="space-y-1">
                <p><span className="text-stone-500">Cliente:</span> <strong>{receiptData.client_name}</strong> ({receiptData.client_category})</p>
                <p><span className="text-stone-500">Mesero:</span> {receiptData.waiter_name}</p>
                <p><span className="text-stone-500">Cajero:</span> {receiptData.cashier_name}</p>
              </div>
              <div className="border-t border-dashed border-stone-300 pt-2">
                {receiptData.orders.map(o => (
                  <div key={o.id} className="flex justify-between py-1 border-b border-stone-100">
                    <span>{o.product_name} x{o.quantity}</span>
                    <span className="font-medium">${o.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-dashed border-stone-300 pt-2 space-y-1">
                <div className="flex justify-between"><span>Subtotal</span><span>${receiptData.subtotal.toFixed(2)}</span></div>
                {receiptData.discount_percent > 0 && (
                  <div className="flex justify-between text-orange-700"><span>Descuento ({receiptData.discount_percent}%)</span><span>-${receiptData.discount_amount.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-lg font-bold border-t border-stone-900 pt-1"><span>Total</span><span>${receiptData.total.toFixed(2)}</span></div>
                <p className="text-right text-stone-500">Pago: {paymentMethods.find(p => p.value === receiptData.payment_method)?.label || receiptData.payment_method}</p>
              </div>
              <p className="text-center text-stone-400 text-xs pt-2">Gracias por su visita</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceipt(false)} className="rounded-full">Cerrar</Button>
            <Button data-testid="print-receipt-btn" onClick={printReceipt} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Reason Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Cancelar Producto</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-stone-600">Ingresa la razon por la cual se cancela este producto:</p>
            <Select value={cancelReason} onValueChange={setCancelReason}>
              <SelectTrigger data-testid="cancel-reason-select" className="rounded-xl"><SelectValue placeholder="Seleccionar razon" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Cliente no lo pidio">Cliente no lo pidio</SelectItem>
                <SelectItem value="Producto en mal estado">Producto en mal estado</SelectItem>
                <SelectItem value="Error del mesero">Error del mesero</SelectItem>
                <SelectItem value="Cliente cambio de opinion">Cliente cambio de opinion</SelectItem>
                <SelectItem value="Producto agotado">Producto agotado</SelectItem>
                <SelectItem value="Cortesia de la casa">Cortesia de la casa</SelectItem>
                <SelectItem value="Otro">Otro</SelectItem>
              </SelectContent>
            </Select>
            {cancelReason === 'Otro' && (
              <Input data-testid="cancel-reason-custom" placeholder="Especifica la razon..." value="" onChange={e => setCancelReason(e.target.value)} className="rounded-xl" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} className="rounded-full">Volver</Button>
            <Button data-testid="cancel-confirm-btn" onClick={confirmCancelOrder} className="bg-red-700 hover:bg-red-800 text-white rounded-full">Confirmar Cancelacion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Experience Dialog */}
      <Dialog open={showExperience} onOpenChange={setShowExperience}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-outfit text-purple-900">Experiencia / Cortesia</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Tipo de cortesia</Label>
              <Select value={expType} onValueChange={v => { setExpType(v); setExpOrderId(''); }}>
                <SelectTrigger data-testid="exp-type-select" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_courtesy">Cortesia completa (cuenta a $0)</SelectItem>
                  <SelectItem value="cancel_entry_fee">Cancelar cuota de entrada</SelectItem>
                  <SelectItem value="cancel_product">Cancelar producto especifico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {expType === 'cancel_product' && selectedVisit && (
              <div>
                <Label>Producto a cancelar</Label>
                <Select value={expOrderId} onValueChange={setExpOrderId}>
                  <SelectTrigger data-testid="exp-order-select" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
                  <SelectContent>
                    {(selectedVisit.orders || []).filter(o => !o.is_entry_fee && o.product_id !== 'entry_fee').map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.product_name} x{o.quantity} - ${o.total.toFixed(2)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Razon</Label>
              <Select value={expReason} onValueChange={setExpReason}>
                <SelectTrigger data-testid="exp-reason-select" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar razon" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mala experiencia del cliente">Mala experiencia del cliente</SelectItem>
                  <SelectItem value="Cliente VIP / Fundador">Cliente VIP / Fundador</SelectItem>
                  <SelectItem value="Promocion especial">Promocion especial</SelectItem>
                  <SelectItem value="Compensacion por servicio">Compensacion por servicio</SelectItem>
                  <SelectItem value="Evento especial">Evento especial</SelectItem>
                  <SelectItem value="Cortesia del gerente">Cortesia del gerente</SelectItem>
                  <SelectItem value="Error en el cobro">Error en el cobro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {expType === 'full_courtesy' && selectedVisit && (
              <div className="bg-purple-50 rounded-xl p-3 text-center">
                <p className="text-xs text-purple-600">Se aplicara cortesia completa por</p>
                <p className="text-lg font-bold text-purple-800">${(selectedVisit.subtotal || 0).toFixed(2)}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExperience(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="exp-submit-btn" onClick={handleExperience} className="bg-purple-700 hover:bg-purple-800 text-white rounded-full">Aplicar Cortesia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
